// RW-12: explicit consent over exactly the units enumerated by the server.
export function unitsFrom(payload) {
  const invalid = () => Object.assign(new Error('Contribution history is unavailable — the server returned an invalid unit list.'), { name: 'ContributionUnitsError' });
  if (!payload || !Array.isArray(payload.units)) throw invalid();
  return Array.from(payload.units, (entry) => {
    if (!entry || typeof entry.key !== 'string' || !['amendment', 'asset_mint'].includes(entry.kind)
      || typeof entry.revision !== 'string' || !(typeof entry.region === 'string' || entry.kind === 'asset_mint' && entry.region === null)
      || (entry.kind === 'amendment' ? typeof entry.amendment_id !== 'string' : typeof entry.revision_digest !== 'string')) throw invalid();
    if (entry.assets !== undefined && (!Array.isArray(entry.assets) || Array.from(entry.assets).some((asset) => !asset || typeof asset.name !== 'string'))) throw invalid();
    const id = entry.kind === 'amendment' ? entry.amendment_id : entry.revision_digest;
    const reason = typeof entry.reason === 'string' && entry.reason.trim() && entry.reason.trim() !== 'derived_identity' ? entry.reason : null;
    const amendmentLabel = entry.scope === 'document' || entry.scope == null && entry.region === 'document'
      ? 'Whole-document edit' : 'Section edit · ' + entry.region;
    return {
      key: entry.key,
      kind: entry.kind,
      id,
      region: entry.region ?? 'document',
      revision: entry.revision,
      label: entry.kind === 'amendment' ? amendmentLabel
        : typeof entry.reason === 'string' && entry.reason.length ? entry.reason : 'Asset change · ' + (entry.assets || []).map((asset) => asset.name).join(', '),
      ...(entry.kind === 'amendment' && reason ? { reason } : {}),
      ...(entry.assets === undefined ? {} : { assets: entry.assets }),
    };
  });
}

export function excludedFrom(payload) {
  const invalid = () => Object.assign(new Error('Contribution history is unavailable — the server returned an invalid unit list.'), { name: 'ContributionUnitsError' });
  if (!payload) throw invalid();
  if (payload.excluded === undefined) return [];
  if (!Array.isArray(payload.excluded)) throw invalid();
  return Array.from(payload.excluded, (entry) => {
    if (!entry || typeof entry.key !== 'string' || typeof entry.amendment_id !== 'string'
      || typeof entry.region !== 'string' || typeof entry.method !== 'string') throw invalid();
    return { key: entry.key, id: entry.amendment_id, region: entry.region, revision: entry.revision, method: entry.method, reason: entry.reason };
  });
}

// Optional read-only detail from the verified approval chain. Refuse orphaned,
// duplicate or contradictory rows; never join proposed/off-chain amendments.
export function editEvidenceFrom(payload) {
  if (payload.edit_evidence === undefined) return [];
  const invalid = () => new Error('Contribution edit evidence is invalid — reload the review.');
  const projection = payload.edit_evidence;
  if (projection?.schema !== 'FlywheelEditProjection.v1' || !Array.isArray(projection.entries)) throw invalid();
  const units = new Map([...(payload.units || []), ...(payload.excluded || [])].map(unit => [unit.key + '@' + unit.revision, unit]));
  const excluded = new Set((payload.excluded || []).map(unit => unit.key + '@' + unit.revision)), seen = new Set();
  const digest = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
  return projection.entries.map(entry => {
    const key = entry?.unit_key + '@' + entry?.revision, unit = units.get(key);
    if (!unit || unit.kind !== 'amendment' || seen.has(key)
      || entry.amendment_id !== unit.amendment_id || entry.revision !== unit.revision || entry.revision_digest !== unit.revision_digest
      || entry.ledger_digest !== entry.unit_key || !digest(entry.base_html_digest) || !digest(entry.after_html_digest)
      || typeof entry.method !== 'string' || !entry.method
      || (excluded.has(key) ? entry.method !== unit.method : entry.method !== 'code')) throw invalid();
    seen.add(key);
    const fields = entry.recorded_fields;
    if (fields !== undefined && (!Array.isArray(fields) || fields.some(field => !field || typeof field.block_id !== 'string'
      || !field.changes || typeof field.changes !== 'object' || Array.isArray(field.changes)
      || Object.values(field.changes).some(value => value !== null && !['string', 'number', 'boolean'].includes(typeof value))))) throw invalid();
    return { ...entry, kind: entry.method === 'chase_proposal' ? 'chase' : fields?.length ? 'field' : 'code' };
  });
}

// Consent units and head identity are authoritative; optional display evidence
// is not. A bad detail projection must not prevent approval or explicit opt-out.
export function rwLoadApproveSheet(payload, revisionDigest = null) {
  const units = unitsFrom(payload), excluded = excludedFrom(payload);
  if (revisionDigest && payload.head?.revision_digest !== revisionDigest) {
    throw new Error('The build moved — reload and review the current revision before contributing.');
  }
  let evidence = null;
  try { evidence = editEvidenceFrom(payload); } catch { /* read-only detail unavailable */ }
  return { state: 'ready', units, excluded, evidence, unverified: (payload.unverified_ledger_members || []).length };
}

export function contributionsBody(units, choices = {}) {
  return units.map((unit) => {
    const included = !Object.hasOwn(choices, unit.key) || choices[unit.key] !== false;
    return unit.kind === 'asset_mint' ? { revision_digest: unit.id, included } : { amendment_id: unit.id, included };
  });
}

export function mapContributionError(status, body = {}) {
  const data = body || {};
  const code = data.code || 'unknown';
  const copy = {
    contribution_unknown: 'An edit is no longer available for contribution — reload the review.',
    contribution_excluded: 'This edit was excluded from Flywheel by policy (Chase-accepted edits never contribute) — reload the review.',
    contribution_invalid: 'The contribution choices are invalid — review the selected edits.',
    consent_conflict: 'Edits of one region contribute together — include or exclude every edit in each region.',
    opt_out_reason_too_long: 'The opt-out reason is too long — shorten it and try again.',
  };
  const ids = (value) => Array.isArray(value) && value.length ? value.join(', ') : 'none';
  const detail = status === 400 && code === 'consent_conflict'
    ? (Array.isArray(data.conflicts) ? data.conflicts : []).map((conflict) =>
      String(conflict.region) + ' · included: ' + ids(conflict.included) + ' · excluded: ' + ids(conflict.excluded) + ' · undecided: ' + ids(conflict.undecided)).join('\n')
    : status === 400 && code === 'contribution_excluded'
      ? [data.error, data.ref && 'ref: ' + data.ref, data.method && 'method: ' + data.method].filter(Boolean).join(' · ')
    : data.error || '';
  return { code, title: code + (status === 400 && copy[code] ? ': ' + copy[code] : (status == null ? '' : ' (' + status + ')')), detail };
}

export function receiptLine(flywheel) {
  if (!flywheel) return '';
  let line = flywheel.contributed + ' of ' + flywheel.units + ' edits contributed';
  if (flywheel.mode === 'opt_out') line += ' · opt-out' + (flywheel.opt_out_reason ? ': ' + flywheel.opt_out_reason : '');
  const emission = flywheel.emission;
  if (emission && emission.status === 'done') line += ' · emission done';
  if (emission && emission.status === 'failed') {
    const summary = emission.blockers_summary;
    line += ' · emission failed' + (summary ? ': ' + (typeof summary === 'string' ? summary : JSON.stringify(summary)) : '');
  }
  const excluded = emission && emission.excluded_summary && emission.excluded_summary.amendments;
  if (excluded > 0) line += ' · ' + excluded + ' Chase edit' + (excluded === 1 ? '' : 's') + ' excluded by policy';
  return line;
}

if (typeof window !== 'undefined') window.GooseReviewContributions = { unitsFrom, excludedFrom, editEvidenceFrom, rwLoadApproveSheet, contributionsBody, mapContributionError, receiptLine };
